import React, {
  createContext, useContext, useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

export const StepsContext = createContext(null);

export const useStepsContext = () => {
  const context = useContext(StepsContext);
  if (!context) {
    throw new Error('useStepsContext must be used within a StepsProvider');
  }
  return context;
};

export function StepsProvider({ children, schema, initialStep = 0 }) {
  const [currentStep, setCurrentStep] = useState(schema?.steps?.[initialStep]?.id || '');
  const [allStepsCompleted, setAllStepsCompleted] = useState(
    initialStep >= schema?.steps?.length,
  );
  const [stepErrors, setStepErrors] = useState({});
  // Track which steps are completed so children can infer edit vs initial render
  const [completedSteps, setCompletedSteps] = useState(
    (schema?.steps?.slice(0, initialStep) || []).map((s) => s.id),
  );

  const setStepError = useCallback((stepId, message) => {
    setStepErrors((prev) => ({ ...prev, [stepId]: message }));
  }, []);

  const clearStepError = useCallback((stepId) => {
    setStepErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[stepId];
      return newErrors;
    });
  }, []);

  const value = useMemo(
    () => ({
      schema,
      currentStep,
      setCurrentStep,
      stepErrors,
      setStepError,
      clearStepError,
      completedSteps,
      setCompletedSteps,
      isStepCompleted: (stepId) => completedSteps.includes(stepId),
      allStepsCompleted,
      setAllStepsCompleted,
    }),
    [
      schema,
      currentStep,
      stepErrors,
      completedSteps,
      setStepError,
      clearStepError,
      allStepsCompleted,
      setAllStepsCompleted,
    ],
  );

  return (
    <StepsContext.Provider value={value}>
      {children}
    </StepsContext.Provider>
  );
}

StepsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  schema: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
  }).isRequired,
  initialStep: PropTypes.number,
};

StepsProvider.defaultProps = {
  initialStep: 0,
};
